import {
  useMutationMemberInvoicePdfAsMember,
  useQueryMemberHighlightedInvoiceAsMember,
} from "@gymflow/api";
import { downloadPdfBlob, formatCurrency } from "@gymflow/helpers";
import { usePortalRoutes } from "apps/portal/src/hooks";
import { RouteFeature } from "apps/portal/src/routes";
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";

import useScaModal from "../../../../hooks/useScaModal";
import { useClubSettings } from "../../../../providers";
import useGymflowModels from "../../../../store";
import { Button, DownloadIcon } from "../../../atoms";
import CustomTooltip from "../../../atoms/CustomTooltip";
import { CalendarWithClockIcon } from "../../../atoms/icons/CalendarWithClockIcon";
import { ExclamationCircleIcon } from "../../../atoms/icons/ExclamationCircleIcon";
import { ShieldIcon } from "../../../atoms/icons/ShieldIcon";
import { HostedPagesProfileCard } from "../../components/HostedPagesProfileCard";

export type HostedPagesHighlightedInvoiceProps = {};

export const HostedPagesHighlightedInvoice =
  ({}: HostedPagesHighlightedInvoiceProps) => {
    const { t } = useTranslation();
    const { api } = useGymflowModels();
    const settings = useClubSettings();
    const history = useHistory();
    const showScaModal = useScaModal({ asMember: true });
    const { createSiteOrEmbedLink } = usePortalRoutes();

    const { data: highlightedInvoice } =
      useQueryMemberHighlightedInvoiceAsMember({ api });

    const { mutateAsync: mutateAsyncInvoicePdf } =
      useMutationMemberInvoicePdfAsMember({ api });

    const isPastDue = highlightedInvoice?.status === "PAST_DUE";
    const isAuthorizationNeeded =
      highlightedInvoice?.status === "AWAITING_AUTHORIZATION";
    const isScheduledInvoice =
      !highlightedInvoice || highlightedInvoice?.status === "SCHEDULED";
    const formattedIssueDate = highlightedInvoice
      ? DateTime.fromISO(highlightedInvoice.issueDate).toLocaleString(
          DateTime.DATE_MED,
        )
      : undefined;

    return (
      <HostedPagesProfileCard.Container className="flex flex-col">
        <div className="flex w-full flex-row items-center justify-between">
          {isPastDue && (
            <CustomTooltip
              message={t(
                `StripeErrorCodes.Long.${
                  highlightedInvoice.lastPaymentErrorCode ?? "UNKNOWN"
                }`,
              )}
            >
              <div className="bg-error-50 text-error-600 dark:bg-error-900 dark:text-error-200 flex flex-row items-center justify-center gap-2 rounded-full px-3 py-1.5 font-semibold">
                <ExclamationCircleIcon className="stroke-error-600 dark:stroke-error-200 size-5" />
                {t(
                  `StripeErrorCodes.Short.${
                    highlightedInvoice.lastPaymentErrorCode ?? "UNKNOWN"
                  }`,
                )}
              </div>
            </CustomTooltip>
          )}

          {isAuthorizationNeeded && (
            <div className="bg-warning-100 text-warning-700 dark:bg-warning-900 dark:text-warning-25 flex flex-row items-center justify-center gap-2 rounded-full px-3 py-1.5 font-semibold">
              <ShieldIcon
                className="size-5"
                pathClassName="stoke-warning-700 dark:stroke-warning-25"
              />
              {t("pages.hostedPagesProfileReceipts.authorizationNeeded")}
            </div>
          )}
          {isScheduledInvoice && (
            <div className="flex flex-row items-center gap-2">
              <div className="bg-secondary-100 flex size-9 items-center justify-center rounded-full p-2">
                <CalendarWithClockIcon className="fill-secondary-500" />
              </div>
              <div className="dark:text-gray-25 flex font-semibold text-gray-950">
                {t("pages.hostedPagesProfileReceipts.scheduledInvoice")}
              </div>
            </div>
          )}
          {highlightedInvoice && (
            <Button
              intent="transparent"
              className="h-8 min-h-0 justify-start"
              onClick={async () => {
                const invoiceNumber = highlightedInvoice.number;
                const { data } = await mutateAsyncInvoicePdf({ invoiceNumber });
                downloadPdfBlob(data, `Gymflow-${invoiceNumber}.pdf`);
              }}
            >
              <div className="flex gap-2 text-gray-500 dark:text-gray-400">
                <DownloadIcon pathClassName="stroke-gray-500 dark:stroke-gray-400" />
                {t("pages.hostedPagesProfileReceipts.downloadInvoice")}
              </div>
            </Button>
          )}
        </div>
        <div className="flex w-full flex-row items-center justify-between">
          {!highlightedInvoice && (
            <div className="text-lg font-medium text-gray-500 dark:text-gray-400">
              {t("pages.hostedPagesProfileReceipts.noInvoices")}
            </div>
          )}
          {highlightedInvoice && (
            <>
              <div className="flex flex-col">
                <div className="dark:text-gray-25 text-2xl font-bold text-gray-950">
                  {formatCurrency(
                    highlightedInvoice.totalAmount,
                    settings.defaultCurrency,
                  )}
                </div>
                <div className="text-sm font-medium text-gray-500 dark:text-gray-400">
                  {t(
                    `pages.hostedPagesProfileReceipts.${
                      isScheduledInvoice
                        ? "willBeCollected"
                        : isPastDue
                          ? "wasDue"
                          : isAuthorizationNeeded
                            ? "due"
                            : "unknown"
                    }`,
                    { date: formattedIssueDate },
                  )}
                </div>
              </div>

              {isPastDue && (
                <Button
                  className="min-h-0 w-32 min-w-0"
                  onClick={() => {
                    history.push(
                      createSiteOrEmbedLink(
                        RouteFeature.SiteProfileReceiptOverview.replace(
                          ":invoiceNumber",
                          highlightedInvoice.number,
                        ),
                      ),
                    );
                  }}
                >
                  {t("pages.hostedPagesProfileReceipts.pay")}
                </Button>
              )}
              {isAuthorizationNeeded && (
                <Button
                  className="min-h-0 w-32 min-w-0"
                  onClick={async () => {
                    await showScaModal({
                      invoiceNumber: highlightedInvoice.number,
                    });
                  }}
                >
                  {t("pages.hostedPagesProfileReceipts.approve")}
                </Button>
              )}
            </>
          )}
        </div>
      </HostedPagesProfileCard.Container>
    );
  };
